import type { Timestamp } from 'firebase/firestore';
import type { SharedUser } from './thinkspace';
import type { RecurrenceRule } from './recurrence';

// Re-export shared types for convenience
export type { SharedUser, SharePermission, InviteStatus } from './thinkspace';
export type { RecurrenceRule } from './recurrence';

// Priority levels for tasks
export type TaskPriority = 'low' | 'medium' | 'high';

export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: '#4CAF50',    // Green
  medium: '#FF9800', // Orange
  high: '#F44336',   // Red
};

// Subtask within a todo item
export interface SubTask {
  id: string;
  title: string;
  completed: boolean;
  order: number;
}

// Individual todo item
export interface TodoItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  completedAt: Timestamp | null;
  dueDate: Timestamp | null;
  priority: TaskPriority | null;
  order: number;
  subtasks: SubTask[];
  // Recurrence fields
  recurrence: RecurrenceRule | null;
  isRecurring: boolean;
  seriesId: string | null; // Links all instances in a recurring series
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Main TodoList document
export interface TodoList {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  ownerEmail: string;
  ownerName: string;
  items: TodoItem[];
  sharedWith: SharedUser[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Input types for creating/updating

export interface CreateTodoListInput {
  name: string;
  description?: string;
}

export interface CreateTodoItemInput {
  title: string;
  description?: string;
  dueDate?: Date | null;
  priority?: TaskPriority | null;
  recurrence?: RecurrenceRule | null;
}

export interface UpdateTodoItemInput {
  title?: string;
  description?: string;
  completed?: boolean;
  completedAt?: Timestamp | null;
  dueDate?: Timestamp | null;
  priority?: TaskPriority | null;
  recurrence?: RecurrenceRule | null;
  isRecurring?: boolean;
}

export interface CreateSubTaskInput {
  title: string;
}

export interface InviteUserInput {
  email: string;
  permission: 'read' | 'write';
}

// Helper functions

/**
 * Calculate completion percentage for a todo list
 */
export const getCompletionPercentage = (todoList: TodoList): number => {
  if (todoList.items.length === 0) return 0;
  const completed = todoList.items.filter(item => item.completed).length;
  return Math.round((completed / todoList.items.length) * 100);
};

/**
 * Get count of completed items
 */
export const getCompletedCount = (todoList: TodoList): number => {
  return todoList.items.filter(item => item.completed).length;
};

/**
 * Check if all subtasks are completed
 */
export const areAllSubtasksCompleted = (item: TodoItem): boolean => {
  if (item.subtasks.length === 0) return false;
  return item.subtasks.every(st => st.completed);
};

/**
 * Get subtask completion count
 */
export const getSubtaskCompletedCount = (item: TodoItem): number => {
  return item.subtasks.filter(st => st.completed).length;
};

/**
 * Sort items by order
 */
export const sortItemsByOrder = (items: TodoItem[]): TodoItem[] => {
  return [...items].sort((a, b) => a.order - b.order);
};

/**
 * Sort subtasks by order
 */
export const sortSubtasksByOrder = (subtasks: SubTask[]): SubTask[] => {
  return [...subtasks].sort((a, b) => a.order - b.order);
};

/**
 * Check if a due date is overdue
 */
export const isOverdue = (dueDate: Timestamp | null): boolean => {
  if (!dueDate) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = dueDate.toDate();
  due.setHours(0, 0, 0, 0);
  return due < now;
};

/**
 * Check if a due date is today
 */
export const isDueToday = (dueDate: Timestamp | null): boolean => {
  if (!dueDate) return false;
  const now = new Date();
  const due = dueDate.toDate();
  return (
    now.getFullYear() === due.getFullYear() &&
    now.getMonth() === due.getMonth() &&
    now.getDate() === due.getDate()
  );
};

/**
 * Format due date for display
 */
export const formatDueDate = (dueDate: Timestamp | null): string => {
  if (!dueDate) return '';
  const date = dueDate.toDate();
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (isDueToday(dueDate)) return 'Today';
  if (
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate()
  ) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-NZ', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};
